import { ref, reactive, computed } from '@vue/composition-api'
import store from '@/store'


export default function useFleetDashboard() {
  
  const dateRange = ref([])
  
  
  const isLoading = reactive({
    fleetStatus: false,
    topConsumptionAssets: false,
    topSevereUseAssets: false,
    topIdleUseAssets: false,
    topFailuresAssets: false,
    assetsCount: false
  })
  
  
  const chartData = reactive({
    fleetStatus: null,
    assetsWithFailures: 0,
    totalAssets: 0,
    topConsumptionAssets: [],
    topSevereUseAssets: [],
    topIdleUseAssets: [],
    topFailuresAssets: []
  })
  
  
  const assetsCountData = reactive({
    totalAssets: 0,
    byType: [],
    byModel: []
  })
  
  
  const searchCustomer = ref('')
  const customerId = ref('')
  
  
  const assetManufacturerId = ref('')
  const assetModelId = ref('')
  const assetTypeId = ref('')
  const groupId = ref('')
  
  
  const formatDateForAPI = (date) => {
    if (!date) return ''
    const d = new Date(date)
    const day = d.getDate().toString().padStart(2, '0')
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const year = d.getFullYear()
    return `${day}/${month}/${year}`
  }
  
  
  const getFixedLastThirtyDays = () => {
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - 30)
    
    return {
      startDate: formatDateForAPI(startDate),
      endDate: formatDateForAPI(endDate)
    }
  }
  
  const getDateParams = () => {
    
    if (dateRange.value && typeof dateRange.value === 'string' && dateRange.value.includes(' to ')) {
      const [startStr, endStr] = dateRange.value.split(' to ')
      
      if (startStr && endStr) {
        return {
          startDate: startStr, 
          endDate: endStr     
        }
      }
    } 
    
    
    const endDate = new Date()
    const startDate = new Date()
    startDate.setMonth(startDate.getMonth() - 1)
    
    return {
      startDate: formatDateForAPI(startDate),
      endDate: formatDateForAPI(endDate)
    }
  }
  
  
  const loadFleetStatus = async () => {
    try {
      isLoading.fleetStatus = true
      
      const response = await store.dispatch('fleet-dashboard/fetchFleetStatus', {
        customerId: customerId.value,
        assetManufacturerId: assetManufacturerId.value,
        assetModelId: assetModelId.value,
        assetTypeId: assetTypeId.value,
        groupId: groupId.value,
        startDate: '',
        endDate: ''
      })
      
      
      chartData.fleetStatus = {
        highUsage: response.data.highUsage,
        lowUsage: response.data.lowUsage,
        inactive: response.data.inactive
      }
      
      
      chartData.assetsWithFailures = response.data.assetsWithFailures || 0
      chartData.totalAssets = response.data.totalAssets || 0
      
    } catch (_) {} finally {
      isLoading.fleetStatus = false
    }
  }
  
  
  const loadTopConsumptionAssets = async () => {
    try {
      isLoading.topConsumptionAssets = true
      const { startDate, endDate } = getDateParams()
      
      const response = await store.dispatch('fleet-dashboard/fetchTopConsumptionAssets', {
        customerId: customerId.value,
        assetManufacturerId: assetManufacturerId.value,
        assetModelId: assetModelId.value,
        assetTypeId: assetTypeId.value,
        groupId: groupId.value,
        startDate,
        endDate,
        limit: 10
      })
      
      chartData.topConsumptionAssets = response.data
    } catch (_) {} finally {
      isLoading.topConsumptionAssets = false
    }
  }
  
  
  const loadTopSevereUseAssets = async () => {
    try {
      isLoading.topSevereUseAssets = true
      const { startDate, endDate } = getDateParams()
      
      const response = await store.dispatch('fleet-dashboard/fetchTopSevereUseAssets', {
        customerId: customerId.value,
        assetManufacturerId: assetManufacturerId.value,
        assetModelId: assetModelId.value,
        assetTypeId: assetTypeId.value,
        groupId: groupId.value,
        startDate,
        endDate,
        limit: 10
      })
      
      chartData.topSevereUseAssets = response.data
    } catch (_) {} finally {
      isLoading.topSevereUseAssets = false
    }
  }
  
  const loadTopIdleUseAssets = async () => {
    try {
      isLoading.topIdleUseAssets = true
      const { startDate, endDate } = getDateParams()
      
      const response = await store.dispatch('fleet-dashboard/fetchTopIdleUseAssets', {
        customerId: customerId.value,
        assetManufacturerId: assetManufacturerId.value,
        assetModelId: assetModelId.value,
        assetTypeId: assetTypeId.value,
        groupId: groupId.value,
        startDate,
        endDate,
        limit: 10
      })
      
      chartData.topIdleUseAssets = response.data
    } catch (_) {} finally {
      isLoading.topIdleUseAssets = false
    }
  }
  
  const loadTopFailuresAssets = async () => {
    try {
      isLoading.topFailuresAssets = true
      const { startDate, endDate } = getDateParams()
      
      const response = await store.dispatch('fleet-dashboard/fetchTopFailuresAssets', {
        customerId: customerId.value,
        assetManufacturerId: assetManufacturerId.value,
        assetModelId: assetModelId.value,
        assetTypeId: assetTypeId.value,
        groupId: groupId.value,
        startDate,
        endDate,
        limit: 10
      })
      
      chartData.topFailuresAssets = response.data
    } catch (_) {} finally {
      isLoading.topFailuresAssets = false
    }
  }
  
  const loadAssetsCount = async () => {
    try {
      isLoading.assetsCount = true
      
      const response = await store.dispatch('fleet-dashboard/fetchAssetsCountReport', {
          customerId: customerId.value,
          assetManufacturerId: assetManufacturerId.value,
          assetModelId: assetModelId.value,
          assetTypeId: assetTypeId.value,
          groupId: groupId.value,
      })
      
      
      const data = response.data?.data || []
      
      
      const totalItem = data.find(item => item.category === 'Total Assets')
      assetsCountData.totalAssets = totalItem?.quantity || 0
      
      
      assetsCountData.byType = data
        .filter(item => item.category === 'By Type')
        .map(item => ({
          name: item.detail,
          quantity: item.quantity
        }))
        .sort((a, b) => b.quantity - a.quantity) 
      
      assetsCountData.byModel = data
        .filter(item => item.category === 'By Model')
        .map(item => ({
          name: item.detail,
          quantity: item.quantity
        }))
        .sort((a, b) => b.quantity - a.quantity) 
      
    } catch (_) {} finally {
      isLoading.assetsCount = false
    }
  }
  
  
  const updateDashboard = async () => {
    
    chartData.fleetStatus = null
    chartData.topConsumptionAssets = []
    chartData.topSevereUseAssets = []
    chartData.topIdleUseAssets = []
    chartData.topFailuresAssets = []
    
    await Promise.all([
      loadFleetStatus(),
      loadTopConsumptionAssets(),
      loadTopSevereUseAssets(),
      loadTopIdleUseAssets(),
      loadTopFailuresAssets(),
      loadAssetsCount()
    ])
  }
  
  
  const isLoadingAll = computed(() => 
    isLoading.fleetStatus || 
    isLoading.topConsumptionAssets || 
    isLoading.topSevereUseAssets || 
    isLoading.topIdleUseAssets || 
    isLoading.topFailuresAssets ||
    isLoading.assetsCount
  )
  
  return {
    dateRange,
    searchCustomer,
    customerId,
    assetManufacturerId,
    assetModelId,
    assetTypeId,
    groupId,
    isLoading,
    isLoadingAll,
    chartData,
    assetsCountData,
    updateDashboard,
    loadFleetStatus,
    loadTopConsumptionAssets,
    loadTopSevereUseAssets,
    loadTopIdleUseAssets,
    loadTopFailuresAssets,
    loadAssetsCount
  }
} 